import {
  ActionButton,
  formatDate,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  TextEllipsis,
  useOutputDescriptionProcessingStatesQuery,
  useSystemClassifierItemsBy,
  Form,
  OutputDescriptionProcessingStatesType,
  FormAlerts,
  Detail,
  CancelErrorsOutputDataSchema,
  CancelErrorsOutputDataType,
  useCancelErrorsOutputData,
  useMutationErrors,
  useAlertContext,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import { FC } from 'react';
import translations from './OutputDataActionModal.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { ActiveOutputsTable } from './ActiveOutputsTable';
import { useFormContext, useWatch } from 'react-hook-form';
import { OutputDataActionParams } from '../OutputDataDirectory';

const { addTranslations, t } = i18nNsFactory('output-data-action-modal');
addTranslations(translations);

export interface CancelErrorsModalProps {
  params: OutputDataActionParams;
  onClose: () => void;
}

export const CancelErrorsModal: FC<CancelErrorsModalProps> = (props) => {
  const { params, onClose } = props;
  const { selectAll, hasAccess, ...otherParams } = params;
  const { data, isLoading } =
    useOutputDescriptionProcessingStatesQuery(otherParams);
  const { mutateAsync } = useCancelErrorsOutputData();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors();
  const { resetAlert, setSuccess } = useAlertContext();

  if (!data) {
    return null;
  }

  return (
    <Form
      schema={CancelErrorsOutputDataSchema}
      defaultValues={getDefaultValues(data, selectAll)}
      onSubmit={(data: CancelErrorsOutputDataType) => {
        resetAlert();
        clearMutationErrors();

        return mutateAsync({ data })
          .then(() => {
            onClose();
            setSuccess(t('alert.success.cancelledErrors'));
          })
          .catch((error) => {
            setMutationErrors(error?.response?.data?.errors ?? []);
          });
      }}
      serverErrors={mutationErrors}
    >
      <CancelErrorsModalInner
        data={data}
        isLoading={isLoading}
        onClose={onClose}
        hasGroupAccess={hasAccess}
      />
    </Form>
  );
};

const CancelErrorsModalInner: FC<{
  data: OutputDescriptionProcessingStatesType;
  isLoading?: boolean;
  onClose: () => void;
  hasGroupAccess: boolean;
}> = (props) => {
  const { data, isLoading, onClose, hasGroupAccess } = props;

  const { setValue } = useFormContext<CancelErrorsOutputDataType>();
  const selectedOutputIds = useWatch<CancelErrorsOutputDataType, 'outputIds'>({
    name: 'outputIds',
  });

  const systemClassifierItemsById = useSystemClassifierItemsBy(['FREQUENCY']);

  if (!data) return null;

  return (
    <Modal
      onCancel={onClose}
      width="60rem"
      loading={isLoading}
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <ActionButton type="abort" onClick={onClose} />
            <ActionButton
              type="formSubmit"
              label={t('button.cancelCalculationErrors')}
              disabled={selectedOutputIds.length === 0}
            />
          </Flex>
        </ModalFooter>
      }
    >
      <ModalHeader>
        <ModalTitle>
          <TextEllipsis>
            {t('title', { count: data.outputs.length })}
          </TextEllipsis>
        </ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <Flex vertical gap="small">
          <Detail label={t('group')}>
            {`${data?.groupCode} - ${data.groupName}`}
          </Detail>
          <Detail label={t('valueDate')}>{formatDate(data.valueDate)}</Detail>
          <Detail label={t('frequency')}>
            {systemClassifierItemsById['FREQUENCY'].get(data.frequencyId)?.name}
          </Detail>
        </Flex>
        <Padding top>
          <ActiveOutputsTable
            mode="edit"
            data={data}
            hasGroupAccess={hasGroupAccess}
            selectionMode="multiple"
            selectedOutputIds={selectedOutputIds}
            onSelectedOutputIdsChange={(selectedIds) =>
              setValue('outputIds', selectedIds)
            }
          />
        </Padding>
      </Padding>
    </Modal>
  );
};

const getDefaultValues = (
  data: OutputDescriptionProcessingStatesType,
  selectAll = false,
) => ({
  groupId: data.groupId,
  valueDate: data.valueDate,
  outputIds: selectAll
    ? [...(data?.outputs.map((output) => output.id) || [])]
    : [
        ...(data?.outputs
          .filter((output) => output.activeDataObjectCalculationErrors)
          .map((output) => output.id) || []),
      ],
});
